import type { ShippingCalendarEntry } from "./api";
import { entryKey } from "./api";
import { dateKey, isWeekend } from "./calendarDates";
import ShipmentCard from "./ShipmentCard";

/**
 * カレンダーの 1 日ぶんのマス（カードの落とし先）。
 *
 * マスからあふれたカードは**マスの中を縦スクロール**させる。「他 3 件」で畳むと
 * 混んでいる日ほど中身が見えなくなり、カレンダーで見たい「どの日が詰まって
 * いるか」が分からなくなるため。
 */
export default function DayCell({
  date,
  entries,
  inMonth,
  isToday,
  isPast,
  editable,
  isDropTarget,
  movingKeys,
  onDragStart,
  onDragEnd,
  onDropOn,
  onDragOver,
  onDragLeave,
  onMoveByDays,
  onEditDate,
}: {
  date: Date;
  entries: ShippingCalendarEntry[];
  /** 表示している月の日か（前月末・翌月頭のマスは薄く出す）。 */
  inMonth: boolean;
  isToday: boolean;
  /** 今日より前の日（未出荷が残っていれば遅れている）。 */
  isPast: boolean;
  editable: boolean;
  /** いまカードを持ってきている落とし先。 */
  isDropTarget: boolean;
  /** 付け替えの送信中のカード。 */
  movingKeys: Set<string>;
  onDragStart: (entry: ShippingCalendarEntry) => void;
  onDragEnd: () => void;
  onDropOn: (date: string) => void;
  onDragOver: (date: string) => void;
  onDragLeave: (date: string) => void;
  onMoveByDays: (entry: ShippingCalendarEntry, days: number) => void;
  onEditDate: (entry: ShippingCalendarEntry) => void;
}) {
  const key = dateKey(date);
  const weekend = isWeekend(date);

  return (
    <div
      aria-label={`${key} の出荷`}
      onDragOver={(event) => {
        if (!editable) return;
        // preventDefault しないと落とせない（既定の挙動が drop を拒む）。
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
        onDragOver(key);
      }}
      onDragLeave={() => onDragLeave(key)}
      onDrop={(event) => {
        if (!editable) return;
        event.preventDefault();
        onDropOn(key);
      }}
      className={[
        "flex min-h-0 flex-col border-b border-r border-default-200 p-1",
        weekend ? "bg-default-100" : "bg-white",
        inMonth ? "" : "opacity-60",
        isDropTarget ? "bg-brand-50 ring-1 ring-inset ring-brand-400" : "",
      ].join(" ")}
    >
      <div className="flex shrink-0 items-center gap-1 px-0.5 pb-1">
        <span
          className={[
            "inline-flex size-5 items-center justify-center rounded-full text-xs tabular-nums",
            isToday ? "bg-brand-500 font-bold text-white" : "",
            !isToday && inMonth ? "text-default-700" : "",
            !isToday && !inMonth ? "text-default-400" : "",
          ].join(" ")}
        >
          {date.getDate()}
        </span>
        {entries.length > 0 && (
          <span className="ml-auto text-[10px] text-default-500">
            {entries.length} 件
          </span>
        )}
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto">
        {entries.map((entry) => (
          <ShipmentCard
            key={entryKey(entry)}
            entry={entry}
            editable={editable}
            isMoving={movingKeys.has(entryKey(entry))}
            isLate={isPast && entry.shipped_at == null}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onMoveByDays={onMoveByDays}
            onEditDate={onEditDate}
          />
        ))}
      </div>
    </div>
  );
}
